import React from "react";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * "Text Quote" button — opens the user's native Messages app pre-filled
 * with the customer name, business name, and a tappable estimate link.
 *
 * Uses the `sms:` URL scheme so there's no Twilio / 10DLC dependency.
 *
 * Props:
 *   slug       : tenant slug
 *   quoteId    : quote id (used to fetch the SMS payload from the backend)
 *   disabled   : disable button (e.g. while sending)
 *   variant    : "primary" | "outline" | "ghost"  (visual style)
 *   size       : "sm" | "md"
 *   className  : extra class
 *   labelOverride : optional alternative label text (default "Text Quote")
 *   testId     : data-testid override
 *   onSent()   : optional callback after the SMS deep link is opened
 */
export const TextQuoteButton = ({
  slug,
  quoteId,
  disabled = false,
  variant = "outline",
  size = "sm",
  className = "",
  labelOverride,
  testId = "text-quote-btn",
  onSent,
}) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleClick = async (e) => {
    e.stopPropagation();
    if (disabled) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/tenants/${slug}/quotes/${quoteId}/sms-payload`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 400) {
          toast.error("Add a phone number to this customer first.");
        } else {
          toast.error("Could not prepare text message.");
        }
        return;
      }
      const data = await res.json();
      if (!data.phone) {
        toast.error("This customer has no phone number on file.");
        return;
      }
      const cleanPhone = (data.phone || "").replace(/[^\d+]/g, "");
      const body = encodeURIComponent(data.body || "");
      // Apple uses `&body=` but only after `?`; Android accepts `?body=`.
      // The `?&body=` form works reliably on both per iOS/Android dev docs.
      const isApple = /iPhone|iPad|iPod|Mac/i.test(navigator.userAgent);
      const href = isApple
        ? `sms:${cleanPhone}&body=${body}`
        : `sms:${cleanPhone}?body=${body}`;
      // Use location.href (not window.open) so iOS opens Messages reliably.
      window.location.href = href;
      onSent && onSent();
    } catch (err) {
      toast.error("Could not open Messages app.");
    }
  };

  const baseClasses = "inline-flex items-center justify-center gap-1.5 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const sizeClasses = size === "md" ? "px-4 py-2 text-sm" : "px-3 py-1.5 text-xs";
  const variantClasses =
    variant === "primary"
      ? "bg-slate-900 text-white hover:bg-slate-900"
      : variant === "ghost"
      ? "text-slate-900 hover:bg-blue-50"
      : "bg-white text-slate-900 border border-blue-300 hover:bg-blue-50";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      data-testid={testId}
      className={`${baseClasses} ${sizeClasses} ${variantClasses} ${className}`}
    >
      <MessageSquare className="w-3.5 h-3.5" />
      {labelOverride || "Text Quote"}
    </button>
  );
};

export default TextQuoteButton;
